import 'regenerator-runtime/runtime';
import { all, call, put, select, take, takeEvery } from 'redux-saga/effects';
import {
  DoRequestResponse,
  GlobalAction,
  GlobalActions,
  GlobalPut,
  VorbelegungPayload,
} from '@ps20-frontend/shop-integration-redux';
import {
  App,
  mapVorbelegungdaten,
  ThhHundVorbelegungdaten,
  Vorbelegungdaten,
} from '@ps20-frontend/frontend-models';
import { SpartenType, ThhHundVorvertragsdaten, VertragsdatenRequestModel } from '@ps20/bff-models';
import { safeSaga } from '@ps20-frontend/safe-saga';
import { ThhHundActions } from '../thhHundActions';
import { AnzahlTiereEnum } from '../reducers/versicherungsnehmer.reducer';
import { IAppState } from '../appState';

const globalActions = new GlobalActions();

export enum VorbelegungThhHundRequestEventsEnum {
  VORBELEGUNG_THH_HUND_REQUEST_SUCCESSFULL = 'VORBELEGUNG_THH_HUND_REQUEST_SUCCESSFULL',
  VORBELEGUNG_THH_HUND_REQUEST_FAILED = 'VORBELEGUNG_THH_HUND_REQUEST_FAILED',
}

export function* watchVorbelegungRequested() {
  yield takeEvery(GlobalActions.VORBELEGUNG_REQUESTED, safeSaga(vorbelegungSagaHund, App.THH_HUND));
  yield takeEvery(
    VorbelegungThhHundRequestEventsEnum.VORBELEGUNG_THH_HUND_REQUEST_SUCCESSFULL,
    safeSaga(successCallback, App.THH_HUND),
  );
  yield takeEvery(
    VorbelegungThhHundRequestEventsEnum.VORBELEGUNG_THH_HUND_REQUEST_FAILED,
    safeSaga(errorCallback, App.THH_HUND),
  );
}

export function* vorbelegungSagaHund(action: GlobalAction<VorbelegungPayload>) {
  if (action.payload.appId !== App.THH_HUND) {
    return;
  }

  if (action.payload?.vorhandeneVorbelegungdaten) {
    yield* thhHundDatenVorbelegen(action.payload.vorhandeneVorbelegungdaten);
  } else {
    const globalPut = GlobalPut.getInstance();
    const vertragsdatenRequestModel: VertragsdatenRequestModel = {
      spLaufnummer: action.payload.spLaufnummer,
      vertragsnummer: action.payload.vertragsnummer,
    };
    yield call(
      [globalPut, globalPut.put],
      globalActions.doRequest({
        appId: App.THH_HUND,
        body: vertragsdatenRequestModel,
        successEvent: VorbelegungThhHundRequestEventsEnum.VORBELEGUNG_THH_HUND_REQUEST_SUCCESSFULL,
        errorEvent: VorbelegungThhHundRequestEventsEnum.VORBELEGUNG_THH_HUND_REQUEST_FAILED,
        url: 'privatschutz/api/vertragsdaten',
      }),
    );
  }
}

export function* successCallback(successAction: DoRequestResponse<ThhHundVorvertragsdaten>) {
  const thhHundVorbelegungdaten: ThhHundVorbelegungdaten = mapVorbelegungdaten(
    SpartenType.TIERHALTERHAFTPFLICHT_HUND,
    successAction.payload,
  ) as ThhHundVorbelegungdaten;
  yield* thhHundDatenVorbelegen(thhHundVorbelegungdaten);
}

export function* thhHundDatenVorbelegen(vorbelegungdaten: Vorbelegungdaten) {
  const state: IAppState = yield select();
  let anzahlTiere: AnzahlTiereEnum = null;
  const thhVorbelegungsdaten = vorbelegungdaten as ThhHundVorbelegungdaten;
  if (thhVorbelegungsdaten.anzTiere) {
    anzahlTiere =
      thhVorbelegungsdaten.anzTiere === 1 ? AnzahlTiereEnum.EINES : AnzahlTiereEnum.MEHRERE;
    yield put(new ThhHundActions().anzahlTiereChanged(anzahlTiere));
  }
  if (anzahlTiere === AnzahlTiereEnum.EINES) {
    yield put(new ThhHundActions().rasseEinHundChanged(thhVorbelegungsdaten.tierrasse[0]));
  }
  if (anzahlTiere === AnzahlTiereEnum.MEHRERE) {
    yield put(new ThhHundActions().anzahlMehrereHundeChanged(thhVorbelegungsdaten.anzTiere));
    yield all(
      thhVorbelegungsdaten.tierrasse.map((tierRasse, index) =>
        put(new ThhHundActions().rasseMehrereHundeChanged(tierRasse, index)),
      ),
    );
  }

  const globalPut = GlobalPut.getInstance();
  if (!state.produkt?.produktlinien) {
    yield take(GlobalActions.PRODUKT_RECEIVED);
  }
  if (vorbelegungdaten.internerVertragsbezugs) {
    yield put(
      globalActions.internerVertragsbezugReceived(
        vorbelegungdaten.internerVertragsbezugs,
        App.THH_HUND,
        state.angebotsId,
      ),
    );
  }
  if (!state.vertragsoptionen?.beibehalten) {
    yield call(
      [globalPut, globalPut.put],
      globalActions.navigationRequested({
        appId: App.THH_HUND,
        subroute: '/eingabe',
      }),
    );
    yield call(
      [globalPut, globalPut.put],
      globalActions.thhHundVorbelegungFinished({
        appId: App.THH_HUND,
        vorhandeneVorbelegungdaten: vorbelegungdaten,
      }),
    );
  } else {
    yield call(
      [globalPut, globalPut.put],
      globalActions.vorbelegungForBeibehaltungRequested(thhVorbelegungsdaten),
    );
  }
  yield call(
    [globalPut, globalPut.put],
    globalActions.vorbelegungFinished({ appId: App.THH_HUND }),
  );
}

export function* errorCallback() {
  const globalPut = GlobalPut.getInstance();
  yield call(
    [globalPut, globalPut.put],
    globalActions.vorbelegungFinished({ appId: App.THH_HUND }),
  );
  yield call(
    [globalPut, globalPut.put],
    globalActions.navigationRequested({
      appId: App.THH_HUND,
      subroute: '/eingabe',
    }),
  );
}
